import type { PageField } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import type { CommandFilters } from './logic';
import { agg, basisValue } from './logic';
import type { CommandDeparture, Severity } from './types';

type FilterField = PageField;
type FilterSchema = Record<string, FilterField>;

export function toursPageView({
  ready,
  sample,
  snapshotDate,
  filters,
  attentionFlag,
  rows,
  data,
  summary,
  regions,
  range,
}: {
  ready: boolean;
  sample: boolean;
  snapshotDate: string;
  filters: CommandFilters;
  attentionFlag: string | null;
  rows: CommandDeparture[];
  data: CommandDeparture[];
  summary: ReturnType<typeof agg>;
  regions: string[];
  range: { from: string; to: string };
}) {
  const filterSchema: FilterSchema = {
    region: { type: 'enum', values: ['All', ...regions] },
    tourId: { type: 'enum', values: [null, ...new Set(data.map((row) => row.opp_id))] },
    dateFrom: { type: 'date' },
    dateTo: { type: 'date' },
    basis: { type: 'enum', values: ['ss', 'rep'] },
    onlyAttention: { type: 'boolean' },
    confidenceTier: { type: 'enum', values: ['all', 'high', 'medium', 'low', 'indicative'] },
    action: {
      type: 'enum',
      values: ['all', 'red', 'amber', 'green', 'empty', 'early', 'closed', 'handed back', 'ltle copy', 'unavailable'],
    },
    severity: {
      type: 'enum',
      values: [null, 'red', 'amber', 'empty', 'loss_today', 'predicted_loss', 'other_action', 'watch', 'none'],
    },
  };
  return {
    pageId: 'tours-command-centre',
    title: 'Tours Command Centre',
    ready,
    dataMode: sample ? 'fictional sample data' : 'reporting snapshot',
    snapshotDate,
    filters: { ...filters, dateFrom: range.from, dateTo: range.to, severity: attentionFlag },
    filterSchema,
    definitions: {
      basis: { ss: 'Sliding Scale', rep: 'Reported' },
      dates: 'Inclusive departure dates in Sydney',
      confidence:
        'filters.confidenceTier is the active selection; summary.confidenceTier is the weighted prediction confidence badge, not a filter.',
      scope:
        'Summary and visibleDepartures describe the scoped departure metrics, including severity. onlyAttention filters the tour list only, not those metrics. availablePopulation permits analytical comparisons without editing the page. Null margin is unknown, not zero.',
    },
    summary,
    readings: { bookedLoadFactor: summary.lf, predictedLoadFactor: summary.plf },
    availableTours: [...new Map(data.map((row) => [row.opp_id, { id: row.opp_id, name: row.opp_name }])).values()],
    departureCount: rows.length,
    visibleDepartures: rows.slice(0, 100).map((row) => ({
      tour: row.opp_name,
      region: row.region,
      departureDate: row.dep,
      travellers: row.pax,
      capacity: row.max_pax,
      ttv: row.ttv,
      margin: basisValue(row, 'le_margin', filters.basis),
      action: basisValue(row, 'flag', filters.basis),
    })),
    visibleDeparturesTruncated: rows.length > 100,
    availablePopulation: regions.map((region) => ({
      region,
      ...agg(
        data.filter((row) => row.region === region && row.dep >= range.from && row.dep <= range.to),
        filters.basis,
      ),
    })),
  };
}

export function validateToursFilters(
  changes: Record<string, unknown>,
  schema: FilterSchema,
  current: CommandFilters,
): Partial<CommandFilters> & { severity?: Severity | 'red' | 'amber' | 'empty' | null } {
  validatePageFilters(changes, schema);
  const next = { ...current, ...changes };
  if (next.dateFrom && next.dateTo && next.dateFrom > next.dateTo)
    throw new Error('The start date must be before the end date.');
  return changes as Partial<CommandFilters> & { severity?: Severity | 'red' | 'amber' | 'empty' | null };
}
